import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';

// Store
import { Dispatch, RootState } from '../../store/store';

// Types
import { IActionsDropdown, IActionsTag } from '../../types/tableDropdown';

// Constants
import ROUTE from '../../constants/route-constants';
import { PAGE_TITLE } from '../../constants/common';
import { Action } from '../../enums/action';

// Components
import Table from '../../components/Table/Table';
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import Modal from '../../components/Modal/Modal';
import ApplyControllerSetupModal from '../../components/ApplyControllerSetupModal/ApplyControllerSetupModal';
import IconEditOutlined from '../../components/Icons/IconEditOutlined/IconEditOutlined';
import IconBin from '../../components/Icons/IconBin/IconBin';
import IconPlay from '../../components/Icons/IconPlay/IconPlay';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import Tooltip from '../../components/Tooltip';
import { ElementSize } from '../../types/element-size';
import { DateFormatter } from '../../utils/date-formatter';
import { useAbilityContext } from '../../components/Can/Can';

type ComponentProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const ControllerSetupsView: React.FC<ComponentProps> = ({
  controllerSetups,
  getControllerSetups,
  deleteControllerSetup,
}) => {
  const navigate = useNavigate();
  const ability = useAbilityContext();

  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [setupToDelete, setSetupToDelete] = useState<null | any>(null);
  const [setupToApplyId, setSetupToApplyId] = useState<string | null>(null);

  const canDelete = ability?.can(Action.Delete, PAGE_TITLE.CONTROLLER_SETUPS);

  const fetchControllerSetups = async () => {
    await getControllerSetups();
    setIsDataLoaded(true);
  };

  useEffect(() => {
    fetchControllerSetups();
  }, []);

  const controllerSetupsView = useMemo(() => {
    return (controllerSetups ?? []).map((setup) => ({
      Name: setup.name,
      'Last Updated': DateFormatter({ date: setup.updated_at }),
      id: setup.id,
    }));
  }, [controllerSetups]);

  const openModalDeletion = (setup) => setSetupToDelete(setup);
  const closeModalDeletion = () => setSetupToDelete(null);

  const acceptDeleteSetup = async () => {
    if (setupToDelete) {
      await deleteControllerSetup({ id: setupToDelete.id });
    }
    closeModalDeletion();
  };

  const actionsButtons: IActionsDropdown[] = [
    {
      icon: () => (
        <Tooltip hint="Apply to controller(s)">
          <IconPlay iconClassName="text-inherit" size={ElementSize.XXS} />
        </Tooltip>
      ),
      onClick: (id: string) => setSetupToApplyId(id),
      loading: false,
      isPinned: true,
    },
    {
      icon: () => (
        <Tooltip hint="Edit">
          <IconEditOutlined stroke="inherit" iconClassName="text-inherit" size={ElementSize.XXS} />
        </Tooltip>
      ),
      onClick: (id: string) => {
        navigate(generatePath(ROUTE.EDIT_CONTROLLER_SETUP, { id }));
      },
      loading: false,
      tag: IActionsTag.Link,
    },
    {
      icon: () => (
        <Tooltip hint="Delete" hintClassName="right-[-90%] bottom-7" isCustomSettings>
          <IconBin fill="#000" size={ElementSize.XXS} />
        </Tooltip>
      ),
      onClick: (...args) => openModalDeletion(args[1]),
      loading: false,
      isShow: () => !!canDelete,
    },
  ];

  return (
    <div className="p-controller-setups-view flex h-full w-full flex-col">
      {!isDataLoaded && (
        <div className="flex flex-auto items-center justify-center">
          <SpinnerLoader />
        </div>
      )}
      {isDataLoaded && (
        <>
          <div className="mb-5 flex w-full items-center justify-between">
            <h2 className="text-title-md2 font-semibold text-black dark:text-white">Controller Setup</h2>
            <InteractiveButton text="Create" onClick={() => navigate(ROUTE.CREATE_CONTROLLER_SETUP)} />
          </div>
          <Table values={controllerSetupsView} defaultKeys={['Name', 'Last Updated']} actionsButtons={actionsButtons} />
          <Modal
            modalTitle="Delete controller setup"
            isVisible={setupToDelete !== null}
            content={`Are you sure you want to delete "${setupToDelete?.Name ?? ''}"?`}
            acceptBtnText="Delete"
            onClose={closeModalDeletion}
            onAccept={acceptDeleteSetup}
            showAcceptBtn
            showCancelBtn
          />
          <ApplyControllerSetupModal
            isVisible={setupToApplyId !== null}
            controllerSetupId={setupToApplyId}
            onClose={() => setSetupToApplyId(null)}
          />
        </>
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  controllerSetups: state.controller_setups.controllerSetups,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getControllerSetups: dispatch.controller_setups.getControllerSetups,
  deleteControllerSetup: dispatch.controller_setups.deleteControllerSetup,
});

export default connect(mapState, mapDispatch)(ControllerSetupsView);
